import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Slide } from 'yet-another-react-lightbox'
import {
  Box,
  Badge,
  OverflowMenu,
  MenuItem,
  useResponsiveValue,
  Text
} from '@machobear/design-system'
import { getImageUrl } from '@/utils/image'
import ImageLightbox from '@/components/image-lightbox'
import { SortableList } from '@/components/sortable-list'
import { Image } from '../../types'

interface ItemProps {
  item: Image
  onDelete: (item: Image) => void
  onSetCoverPhoto: (item: Image) => void
  onThumbnailClick: () => void
}

interface ListProps {
  items: Image[]
  onChange: (items: Image[]) => void
  onDelete: (item: Image) => void
  onSetCoverPhoto: (item: Image) => void
}

function getFileLabel(image: Image) {
  const { name, location } = image
  const awsfilename = location.substring(location.lastIndexOf('/') + 1)
  return name !== 'blob' ? name : awsfilename
}

const PhotoListItem = ({ item, onDelete, onSetCoverPhoto, onThumbnailClick }: ItemProps) => {
  const { t } = useTranslation()
  const isTablet = useResponsiveValue()({
    mobile: true,
    tablet: true,
    desktop: false
  })

  const isCoverPhoto = item.tag === 'cover_photo'

  return (
    <SortableList.Item id={item.id}>
      <Box width="full" display="flex" alignItems="center" gap="small">
        <SortableList.DragHandle id={item.id} />
        <Box
          style={{ height: 48, width: 64 }}
          display="flex"
          justifyContent="center"
          flexShrink={0}
          borderRadius="small"
          overflow="hidden"
          background="neutralLight"
          cursor="pointer"
          onClick={onThumbnailClick}
        >
          <img
            alt=""
            src={getImageUrl(item.aws_s3_key, { width: 128, height: 96 }) ?? ''}
            style={{ objectFit: 'cover' }}
          />
        </Box>
        <Box
          width="full"
          display="flex"
          flexDirection={isTablet ? 'column' : 'row'}
          alignItems={isTablet ? 'flexStart' : 'center'}
          justifyContent={isTablet ? 'flexStart' : 'spaceBetween'}
          gap={isTablet ? 'xxsmall' : 'small'}
          style={{ minWidth: '0' }}
        >
          <Box width="full" style={{ minWidth: '0' }}>
            <Text size="small" weight="medium" maxLines={1}>
              {getFileLabel(item)}
            </Text>
          </Box>
          {isCoverPhoto && (
            <Box textAlign="center">
              <Badge tone="info" variant="solid">
                {t('Cover Photo', 'Cover Photo')}
              </Badge>
            </Box>
          )}
        </Box>
        <Box marginRight="medium">
          <OverflowMenu label="More actions" id="options">
            {item.tag !== 'cover_photo' && (
              <MenuItem
                onClick={() => {
                  onSetCoverPhoto(item)
                }}
              >
                {t('Set as cover photo', 'Set as cover photo')}
              </MenuItem>
            )}
            <MenuItem
              tone="critical"
              onClick={() => {
                onDelete(item)
              }}
            >
              {t('common.remove', 'Remove')}
            </MenuItem>
          </OverflowMenu>
        </Box>
      </Box>
    </SortableList.Item>
  )
}

const getLightboxPhotos = (images: Image[]) => {
  const photoConfig = {
    fit: 'fit-in/1150x785',
    quality: 100
  }
  const photos = images.map(({ aws_s3_key }) => {
    return {
      src: getImageUrl(aws_s3_key, photoConfig)
    }
  })
  return photos
}

const DraggablePhotoList = ({ items, onChange, onDelete, onSetCoverPhoto }: ListProps) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1)
  const largeImages = getLightboxPhotos(items) as Slide[]

  return (
    <>
      <Box style={{ maxHeight: '391px', overflow: 'auto' }}>
        {/* 391px fits exactly 6 PhotoListItem */}
        <SortableList
          items={items}
          onChange={onChange}
          renderItem={(item, index) => (
            <PhotoListItem
              item={item}
              onDelete={onDelete}
              onSetCoverPhoto={onSetCoverPhoto}
              onThumbnailClick={() => setLightboxIndex(index)}
            />
          )}
        />
      </Box>
      <ImageLightbox
        index={lightboxIndex}
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
        slides={largeImages}
      />
    </>
  )
}

export default DraggablePhotoList
